import Bag from '@foxglove/rosbag/dist/cjs/Bag';
import { decompress as bzip2Decompress } from 'seek-bzip';
import lz4 from 'lz4js';
import { parquetWriteBuffer } from 'hyparquet-writer';
import type { ReindexMeta } from './reindexUtils';
import type { BagSource, RosoutMessage, DiagnosticStatusEntry, SeverityLevel, TopicInfo, ProgressCallback } from './types';
import { BagLoadError, DIAGNOSTIC_LEVEL_NAMES, ROS1_SEVERITY } from './types';
import { yieldToEventLoop, createProgressThrottle } from './progressUtils';

/** In-memory Filelike for the reindex re-open path (after building a Uint8Array). */
class Uint8ArrayReader {
  constructor(private bytes: Uint8Array) {}

  read(offset: number, length: number): Promise<Uint8Array> {
    return Promise.resolve(this.bytes.subarray(offset, offset + length));
  }

  size(): number {
    return this.bytes.byteLength;
  }

  release(): void {
    this.bytes = new Uint8Array(0);
  }
}

/** Adapt a lazy `BagSource` to `@foxglove/rosbag`'s Filelike interface. */
function bagSourceToFilelike(source: BagSource) {
  return {
    read: (offset: number, length: number) => source.read(offset, length),
    size: () => source.size,
  };
}

type Timezone = 'local' | 'utc';


type BagConnectionView = {
  topic: string;
  type?: string;
};

type BagTimestamp = {
  sec: number;
  nsec: number;
};

type RosoutPayload = {
  level?: number;
  msg?: string;
  name?: string;
  file?: string;
  line?: number;
  function?: string;
  topics?: string[];
};

type DiagnosticValuePayload = {
  key?: string;
  value?: string;
};

type DiagnosticStatusPayload = {
  name?: string;
  level?: number;
  message?: string;
  values?: DiagnosticValuePayload[];
};

type DiagnosticArrayPayload = {
  status?: DiagnosticStatusPayload[];
};

type ReadMessageResult<T> = {
  message: T;
  timestamp: BagTimestamp;
};

export async function loadRosbagMessages(source: BagSource, onProgress?: ProgressCallback): Promise<{
  messages: RosoutMessage[];
  uniqueNodes: Set<string>;
  diagnostics: DiagnosticStatusEntry[];
  hasDiagnostics: boolean;
  availableTopics: TopicInfo[];
  reindexedBytes?: Uint8Array;
  reindexMeta?: ReindexMeta;
}> {
  const messages: RosoutMessage[] = [];
  const uniqueNodes = new Set<string>();

  console.log(`Loading ROS1 bag: ${source.name} (${source.size} bytes)`);

  // Emit open phase immediately so the UI shows activity right away.
  onProgress?.({ phase: 'open', messageCount: 0, fileSize: source.size });

  try {
    // Peek the magic bytes via lazy read (no full-file load).
    const headerView = await source.read(0, Math.min(100, source.size));
    const headerStr = new TextDecoder().decode(headerView.subarray(0, 13));
    if (headerStr !== '#ROSBAG V2.0\n') {
      // Only reported when it is wrong. This is the first thing to check when a
      // file refuses to open, and the raw bytes say more than the error will.
      const head = Array.from(headerView.subarray(0, 20))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join(' ');
      console.warn(`Unexpected bag header ${JSON.stringify(headerStr)}, first 20 bytes: ${head}`);
    }

    let bag: InstanceType<typeof Bag> | null = new Bag(bagSourceToFilelike(source), {
      decompress: {
        bz2: (buffer: Uint8Array) => bzip2Decompress(buffer),
        lz4: (buffer: Uint8Array) => lz4.decompress(buffer),
      },
    });

    await bag.open();

    // Check if bag is indexed — if not, reindex in-memory and use the reindexed bag
    let activeBag = bag!;
    let reindexedBytes: Uint8Array | undefined;
    let reindexMeta: ReindexMeta | undefined;
    if (bag.header && bag.header.indexPosition === 0 && bag.header.connectionCount === 0 && bag.header.chunkCount === 0) {
      console.log('Bag is unindexed — reindexing in memory');
      // Emit reindex phase BEFORE the heavy materialize+reindex work so the UI
      // can show the phase label while the synchronous scan runs.
      onProgress?.({ phase: 'reindex', messageCount: 0, fileSize: source.size });
      await yieldToEventLoop();
      // Reindex needs a full-buffer scan, so materialize the file here only
      // (the indexed fast-path above never pays this cost).
      const fullBytes = await source.read(0, source.size);
      try {
        const { reindexBagFromBuffer } = await import('./reindexUtils');
        const reindexResult = reindexBagFromBuffer(fullBytes, {
          bz2: (buffer: Uint8Array) => bzip2Decompress(buffer),
          lz4: (buffer: Uint8Array) => lz4.decompress(buffer),
        });
        reindexedBytes = reindexResult.bytes;
        reindexMeta = reindexResult.meta;

        const reindexedReader = new Uint8ArrayReader(reindexedBytes);
        const reindexedBag = new Bag(reindexedReader, {
          decompress: {
            bz2: (buffer: Uint8Array) => bzip2Decompress(buffer),
            lz4: (buffer: Uint8Array) => lz4.decompress(buffer),
          },
        });
        await reindexedBag.open();

        activeBag = reindexedBag;
        bag = null;
        console.log('Reindex complete');
      } catch (reindexError) {
        const { isReindexFailureLike } = await import('./reindexUtils');
        if (isReindexFailureLike(reindexError)) throw reindexError;
        const reason = reindexError instanceof Error ? reindexError.message : String(reindexError);
        throw new BagLoadError('error.failedToReindex', { reason }, { cause: reindexError });
      }
    }

    // Find rosout and diagnostics topics
    const rosoutTopics: string[] = [];
    const diagnosticsTopics: string[] = [];
    for (const conn of activeBag.connections.values()) {
      if (conn.topic.includes('rosout') || conn.type === 'rosgraph_msgs/Log') {
        rosoutTopics.push(conn.topic);
      }
      if (conn.topic.includes('diagnostics') || conn.type === 'diagnostic_msgs/DiagnosticArray') {
        diagnosticsTopics.push(conn.topic);
      }
    }

    console.log(
      `Found ${rosoutTopics.length} rosout and ${diagnosticsTopics.length} diagnostics topic(s) ` +
      `among ${activeBag.connections.size} connections`,
    );

    const availableTopics: TopicInfo[] = Array.from(activeBag.connections.values()).map(
      (conn: BagConnectionView) => ({ topic: conn.topic, type: conn.type ?? 'unknown' }),
    );

    const decompressOptions = {
      bz2: (buffer: Uint8Array) => bzip2Decompress(buffer),
      lz4: (buffer: Uint8Array) => lz4.decompress(buffer),
    };

    // Read rosout messages
    // ROS1 cannot produce processed/total counts, so we emit messageCount only.
    onProgress?.({ phase: 'rosout', messageCount: 0, fileSize: source.size });
    if (rosoutTopics.length > 0) {
      const shouldEmit = createProgressThrottle();

      await activeBag.readMessages(
        { topics: rosoutTopics, decompress: decompressOptions },
        (result: ReadMessageResult<RosoutPayload>) => {
          const msg = result.message;

          if (msg && msg.level !== undefined && msg.msg !== undefined) {
            const rosoutMsg: RosoutMessage = {
              timestamp: result.timestamp.sec + result.timestamp.nsec / 1e9,
              node: msg.name || 'unknown',
              severity: ROS1_SEVERITY[msg.level] ?? 'DEBUG',
              message: msg.msg,
              file: msg.file || '',
              line: msg.line || 0,
              function: msg.function || '',
              topics: msg.topics || [],
            };

            messages.push(rosoutMsg);
            if (msg.name) {
              uniqueNodes.add(msg.name);
            }

            // Throttled progress. Cannot await inside the readMessages callback
            // (sync), so yield only at phase boundaries.
            if (shouldEmit()) {
              onProgress?.({ phase: 'rosout', messageCount: messages.length, fileSize: source.size });
            }
          }
        }
      );
      // Final emit: the throttle above skips any update that lands inside its
      // interval, so without this the UI would keep whatever count the last
      // throttled emit caught (and stay at 0 for bags that load in one tick).
      onProgress?.({ phase: 'rosout', messageCount: messages.length, fileSize: source.size });
      console.log(`Loaded ${messages.length} rosout messages from ${uniqueNodes.size} nodes`);
    }
    // Yield to event loop at the rosout→diagnostics phase boundary so React can
    // flush the final rosout progress update before diagnostics work begins.
    await yieldToEventLoop();

    // Read diagnostics messages (state-change only)
    // messageCount semantics during diagnostics phase = diagnostics.length (state-change entries).
    const diagnostics: DiagnosticStatusEntry[] = [];
    const hasDiagnostics = diagnosticsTopics.length > 0;
    onProgress?.({ phase: 'diagnostics', messageCount: diagnostics.length, fileSize: source.size });

    if (hasDiagnostics) {
      const lastState = new Map<string, { level: number; message: string }>();
      const shouldEmit = createProgressThrottle();

      await activeBag.readMessages(
        { topics: diagnosticsTopics, decompress: decompressOptions },
        (result: ReadMessageResult<DiagnosticArrayPayload>) => {
          const msg = result.message;
          if (!msg || !msg.status) return;

          const timestamp = result.timestamp.sec + result.timestamp.nsec / 1e9;

          for (const status of msg.status) {
            const name: string = status.name || 'unknown';
            const level: number = status.level ?? 0;
            const message: string = status.message || '';
            const values: { key: string; value: string }[] = (status.values || []).map((v: DiagnosticValuePayload) => ({
              key: v.key || '',
              value: v.value || '',
            }));

            const prev = lastState.get(name);
            if (!prev || prev.level !== level || prev.message !== message) {
              lastState.set(name, { level, message });
              diagnostics.push({ timestamp, name, level, message, values });

              // Throttled progress. Cannot await inside the readMessages
              // callback (sync).
              if (shouldEmit()) {
                onProgress?.({ phase: 'diagnostics', messageCount: diagnostics.length, fileSize: source.size });
              }
            }
          }
        }
      );
      // Final emit, for the same reason as the rosout phase above.
      onProgress?.({ phase: 'diagnostics', messageCount: diagnostics.length, fileSize: source.size });
      console.log(`Loaded ${diagnostics.length} diagnostics state changes`);
    }

    return { messages, uniqueNodes, diagnostics, hasDiagnostics, availableTopics, reindexedBytes, reindexMeta };
  } catch (error) {
    console.error('Failed to load ROS1 bag:', error);
    throw error;
  }
}

export function filterMessages(
  messages: RosoutMessage[],
  filters: {
    nodeNames?: Set<string>;
    severityLevels?: Set<SeverityLevel>;
    messageKeywords?: string[];
    messageRegex?: string;
    filterMode?: 'OR' | 'AND';
    useRegex?: boolean;
    startTime?: number;
    endTime?: number;
  }
): RosoutMessage[] {
  return messages.filter((msg) => {
    const conditions: boolean[] = [];

    // Severity filter
    if (filters.severityLevels && filters.severityLevels.size > 0) {
      conditions.push(filters.severityLevels.has(msg.severity));
    }

    // Node filter
    if (filters.nodeNames && filters.nodeNames.size > 0) {
      conditions.push(filters.nodeNames.has(msg.node));
    }

    // Message text filter (keywords or regex)
    if (filters.useRegex && filters.messageRegex && filters.messageRegex.trim()) {
      try {
        const regex = new RegExp(filters.messageRegex, 'i');
        conditions.push(regex.test(msg.message));
      } catch (e) {
        // Invalid regex, skip this filter
        console.warn('Invalid regex pattern:', filters.messageRegex);
      }
    } else if (!filters.useRegex && filters.messageKeywords && filters.messageKeywords.length > 0) {
      // Normalize keywords: trim, drop empty entries (e.g. from trailing commas), and lowercase
      const keywords = filters.messageKeywords
        .map(k => (k ?? '').toString().trim())
        .filter(k => k.length > 0)
        .map(k => k.toLowerCase());

      // If all entries were empty (e.g. user typed "," or "error,"), skip this keyword filter
      if (keywords.length > 0) {
        const messageLower = msg.message.toLowerCase();
        const hasKeyword = keywords.some(keyword => messageLower.includes(keyword));
        conditions.push(hasKeyword);
      }
    }

    // Time range filters
    if (filters.startTime !== undefined && msg.timestamp < filters.startTime) {
      return false;
    }
    if (filters.endTime !== undefined && msg.timestamp > filters.endTime) {
      return false;
    }

    // Apply filter mode (OR/AND)
    if (conditions.length === 0) {
      return true; // No filters applied
    }

    if (filters.filterMode === 'AND') {
      return conditions.every(c => c);
    } else {
      // Default to OR mode
      return conditions.some(c => c);
    }
  });
}

export function filterDiagnostics(
  diagnostics: DiagnosticStatusEntry[],
  filters: {
    levels?: Set<number>;
    names?: Set<string>;
    messageKeywords?: string;
    messageRegex?: string;
    filterMode?: 'OR' | 'AND';
    useRegex?: boolean;
    startTime?: number;
    endTime?: number;
  }
): DiagnosticStatusEntry[] {
  return diagnostics.filter(d => {
    // Time range filters (hard filters, not subject to OR/AND)
    if (filters.startTime !== undefined && d.timestamp < filters.startTime) {
      return false;
    }
    if (filters.endTime !== undefined && d.timestamp > filters.endTime) {
      return false;
    }

    const conditions: boolean[] = [];

    if (filters.levels && filters.levels.size > 0) {
      conditions.push(filters.levels.has(d.level));
    }
    if (filters.names && filters.names.size > 0) {
      conditions.push(filters.names.has(d.name));
    }
    if (filters.useRegex && filters.messageRegex && filters.messageRegex.trim()) {
      try {
        const regex = new RegExp(filters.messageRegex, 'i');
        conditions.push(regex.test(d.message));
      } catch { /* skip invalid regex */ }
    } else if (!filters.useRegex && filters.messageKeywords) {
      const keywords = filters.messageKeywords.split(',').map(k => k.trim().toLowerCase()).filter(k => k.length > 0);
      if (keywords.length > 0) {
        const msgLower = d.message.toLowerCase();
        conditions.push(keywords.some(kw => msgLower.includes(kw)));
      }
    }

    if (conditions.length === 0) return true;
    return filters.filterMode === 'AND' ? conditions.every(c => c) : conditions.some(c => c);
  });
}

function formatTimestamp(timestamp: number, timezone: Timezone = 'local'): string {
  const date = new Date(timestamp * 1000);
  if (timezone === 'utc') {
    return date.toISOString().replace('T', ' ').substring(0, 23) + ' UTC';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const ms = String(date.getMilliseconds()).padStart(3, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${ms}`;
}

export function escapeCSV(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function exportToCSV(messages: RosoutMessage[], timezone: Timezone = 'local'): string {
  const headers = ['Timestamp','Time','Node','Severity','Message','File','Line','Function','Topics'];
  const rows = messages.map(msg => [
    msg.timestamp.toFixed(6),
    formatTimestamp(msg.timestamp, timezone),
    escapeCSV(msg.node),
    msg.severity,
    escapeCSV(msg.message),
    escapeCSV(msg.file || ''),
    String(msg.line || 0),
    escapeCSV(msg.function || ''),
    escapeCSV((msg.topics || []).join(';'))
  ]);
  return '\uFEFF' + [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
}

export function exportToJSON(messages: RosoutMessage[], timezone: Timezone = 'local'): string {
  const exportData = messages.map(msg => ({
    timestamp: msg.timestamp,
    time: formatTimestamp(msg.timestamp, timezone),
    node: msg.node,
    severity: msg.severity,
    message: msg.message,
    file: msg.file || '',
    line: msg.line || 0,
    function: msg.function || '',
    topics: msg.topics || []
  }));
  return JSON.stringify(exportData, null, 2);
}

export function exportToTXT(messages: RosoutMessage[], timezone: Timezone = 'local'): string {
  return messages.map(msg => {
    const time = formatTimestamp(msg.timestamp, timezone);
    const location = msg.file ? `${msg.file}:${msg.line || 0}` : '';
    let line = `[${time}] [${msg.severity}] [${msg.node}]: ${msg.message}`;
    if (location) line += ` (${location})`;
    return line;
  }).join('\n');
}

export function exportDiagnosticsToCSV(diagnostics: DiagnosticStatusEntry[], timezone: Timezone = 'local'): string {
  const headers = ['Timestamp', 'Time', 'Name', 'Level', 'Message', 'Values'];
  const rows = diagnostics.map(d => [
    d.timestamp.toFixed(6),
    formatTimestamp(d.timestamp, timezone),
    escapeCSV(d.name),
    DIAGNOSTIC_LEVEL_NAMES[d.level] || String(d.level),
    escapeCSV(d.message),
    escapeCSV(d.values.map(v => `${v.key}=${v.value}`).join('; ')),
  ]);
  return '\uFEFF' + [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
}

export function exportDiagnosticsToJSON(diagnostics: DiagnosticStatusEntry[], timezone: Timezone = 'local'): string {
  const exportData = diagnostics.map(d => ({
    timestamp: d.timestamp,
    time: formatTimestamp(d.timestamp, timezone),
    name: d.name,
    level: DIAGNOSTIC_LEVEL_NAMES[d.level] || d.level,
    message: d.message,
    values: d.values,
  }));
  return JSON.stringify(exportData, null, 2);
}

export function exportDiagnosticsToTXT(diagnostics: DiagnosticStatusEntry[], timezone: Timezone = 'local'): string {
  return diagnostics.map(d => {
    const time = formatTimestamp(d.timestamp, timezone);
    const level = DIAGNOSTIC_LEVEL_NAMES[d.level] || String(d.level);
    let line = `[${time}] [${level}] ${d.name}: ${d.message}`;
    if (d.values.length > 0) {
      line += ` {${d.values.map(v => `${v.key}=${v.value}`).join(', ')}}`;
    }
    return line;
  }).join('\n');
}

export function exportToParquet(messages: RosoutMessage[], timezone: Timezone = 'local'): Uint8Array {
  const buf = parquetWriteBuffer({
    columnData: [
      { name: 'timestamp', data: messages.map(m => m.timestamp), type: 'DOUBLE' },
      { name: 'time_text', data: messages.map(m => formatTimestamp(m.timestamp, timezone)), type: 'STRING' },
      { name: 'node', data: messages.map(m => m.node), type: 'STRING' },
      { name: 'severity', data: messages.map(m => m.severity), type: 'STRING' },
      { name: 'message', data: messages.map(m => m.message), type: 'STRING' },
      { name: 'file', data: messages.map(m => m.file || ''), type: 'STRING' },
      { name: 'line', data: messages.map(m => m.line || 0), type: 'INT32' },
      { name: 'function_name', data: messages.map(m => m.function || ''), type: 'STRING' },
      { name: 'topics_text', data: messages.map(m => (m.topics || []).join(';')), type: 'STRING' },
    ],
  });
  return new Uint8Array(buf);
}

export function exportDiagnosticsToParquet(
  diagnostics: DiagnosticStatusEntry[],
  timezone: Timezone = 'local'
): Uint8Array {
  const buf = parquetWriteBuffer({
    columnData: [
      { name: 'timestamp', data: diagnostics.map(d => d.timestamp), type: 'DOUBLE' },
      { name: 'time_text', data: diagnostics.map(d => formatTimestamp(d.timestamp, timezone)), type: 'STRING' },
      { name: 'name', data: diagnostics.map(d => d.name), type: 'STRING' },
      { name: 'level_code', data: diagnostics.map(d => d.level), type: 'INT32' },
      { name: 'level_name', data: diagnostics.map(d => DIAGNOSTIC_LEVEL_NAMES[d.level] || String(d.level)), type: 'STRING' },
      { name: 'message', data: diagnostics.map(d => d.message), type: 'STRING' },
      { name: 'values_json', data: diagnostics.map(d => d.values), type: 'JSON' },
    ],
  });
  return new Uint8Array(buf);
}

export async function loadMessages(source: BagSource, onProgress?: ProgressCallback): Promise<{
  messages: RosoutMessage[];
  uniqueNodes: Set<string>;
  diagnostics: DiagnosticStatusEntry[];
  hasDiagnostics: boolean;
  availableTopics: TopicInfo[];
  reindexedBytes?: Uint8Array;
  reindexMeta?: ReindexMeta;
}> {
  if (source.size === 0) {
    throw new BagLoadError('error.emptyFile');
  }
  const name = source.name.toLowerCase();
  if (name.endsWith('.mcap') || name.endsWith('.mcap.zstd')) {
    const { loadMcapMessages } = await import('./mcapUtils');
    return await loadMcapMessages(source, onProgress);
  }
  return await loadRosbagMessages(source, onProgress);
}
